import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from '@/data/blogPosts';
import { BlogPost as BlogPostType } from '@/components/BlogCard';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Calendar, 
  Share2, 
  Bookmark,
  BookmarkCheck,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Info,
  BookOpen,
  Building2,
  PlayCircle,
  PenLine
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const SAVED_ARTICLES_KEY = 'kaufpause-saved-articles';

// Funktion zum Laden eines Blogartikels anhand seiner Slug-URL
const getBlogPostBySlug = async (slug: string): Promise<BlogPostType | undefined> => {
  const post = blogPosts.find(post => post.slug === slug);
  
  if (!post) {
    return undefined;
  }
  
  // Stelle sicher, dass categories immer vorhanden ist
  const postWithCategories = {
    ...post,
    categories: post.categories || [post.category || 'Allgemein']
  };
  
  try {
    // Lade den Markdown-Inhalt falls vorhanden
    const content = await import(`../blog/${post.slug}.md?raw`);
    return {
      ...postWithCategories,
      content: content.default
    };
  } catch (error) {
    console.error(`Fehler beim Laden des Blog-Artikels: ${slug}`, error);
    // Fallback: Verwende den Content aus blogPosts.ts
    return postWithCategories;
  }
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        if (!slug) {
          throw new Error('Kein Artikel-Slug angegeben');
        }
        
        const blogPost = await getBlogPostBySlug(slug);
        
        if (!blogPost) {
          throw new Error('Artikel nicht gefunden');
        }
        
        setPost(blogPost);
        setError(null);
        
        // Prüfe, ob der Artikel gespeichert ist
        checkIfArticleIsSaved(blogPost.slug);
      } catch (err) {
        console.error('Fehler beim Laden des Artikels:', err);
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);
  
  // Prüft, ob der Artikel bereits gespeichert ist
  const checkIfArticleIsSaved = (slug: string) => {
    try {
      const savedArticles = localStorage.getItem(SAVED_ARTICLES_KEY);
      if (savedArticles) {
        const articles = JSON.parse(savedArticles);
        setIsSaved(articles.some((article: {slug: string}) => article.slug === slug));
      }
    } catch (err) {
      console.error('Fehler beim Prüfen des gespeicherten Artikels:', err);
    }
  };
  
  // Speichern oder Entfernen des Artikels
  const toggleSaveArticle = () => {
    if (!post) return;
    
    try {
      const savedArticles = localStorage.getItem(SAVED_ARTICLES_KEY);
      let articles = savedArticles ? JSON.parse(savedArticles) : [];
      
      if (isSaved) {
        // Artikel aus der gespeicherten Liste entfernen
        articles = articles.filter((article: {slug: string}) => article.slug !== post.slug);
        toast.success('Artikel wurde aus deinen Lesezeichen entfernt');
      } else {
        // Artikel zur gespeicherten Liste hinzufügen
        const articleToSave = {
          id: post.id,
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt,
          date: post.date,
          imageUrl: post.imageUrl,
          categories: post.categories
        };
        articles.push(articleToSave);
        toast.success('Artikel wurde zu deinen Lesezeichen hinzugefügt');
      }
      
      localStorage.setItem(SAVED_ARTICLES_KEY, JSON.stringify(articles));
      setIsSaved(!isSaved);
    } catch (err) {
      console.error('Fehler beim Speichern des Artikels:', err);
      toast.error('Der Artikel konnte nicht gespeichert werden');
    }
  };

  const handleShare = (platform: string) => {
    if (!post) return;
    
    const url = window.location.href;
    const title = post.title;
    const excerpt = post.excerpt.substring(0, 100) + '...';
    
    // Standardtext für das Teilen erstellen
    const shareText = `${title} - ${excerpt}\n\nMehr lesen auf KaufPause:\n${url}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title + ' - ' + excerpt)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title + ' - ' + excerpt)}&via=KaufPause`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(excerpt)}`;
        break;
      case 'copy':
        try {
          navigator.clipboard.writeText(shareText);
          toast.success('In die Zwischenablage kopiert! Füge den Text mit STRG + V (Windows) oder Command + V (Mac) ein.');
        } catch (err) {
          toast.error('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
          console.error('Fehler beim Kopieren:', err);
        }
        return;
    }
    
    // Für alle Social Media Plattformen: Erst kopieren, dann Link öffnen
    if (shareUrl) {
      try {
        navigator.clipboard.writeText(shareText);
        toast.success(`Inhalt kopiert! Der ${platform === 'facebook' ? 'Facebook' : platform === 'twitter' ? 'Twitter' : 'LinkedIn'}-Dialog wird geöffnet.`);
      } catch (err) {
        console.error('Fehler beim Kopieren:', err);
        toast.info(`${platform === 'facebook' ? 'Facebook' : platform === 'twitter' ? 'Twitter' : 'LinkedIn'}-Dialog wird geöffnet.`);
      }
      
      // Kurze Verzögerung, damit der Toast sichtbar ist
      setTimeout(() => {
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {error || 'Artikel nicht gefunden'}
          </h2>
          <p className="text-gray-600 mb-6">
            Leider konnten wir den gewünschten Artikel nicht finden.
          </p>
          <Button onClick={() => navigate('/blog')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Button>
        </div>
      </div>
    );
  }

  // Format date
  const formattedDate = () => {
    try {
      return format(new Date(post.date), 'dd. MMMM yyyy', { locale: de });
    } catch (error) {
      return post.date;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation - identisch zu anderen Seiten */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 -translate-y-0.5"
              />
              <span className="text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
              </span>
            </a>
          </div>
          
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-emerald-600 bg-emerald-50 transition-colors duration-200 font-medium px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <PlayCircle className="w-4 h-4" />
              Demo testen
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/auth')}
              className="text-gray-600 hover:text-emerald-600"
            >
              Anmelden
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 pt-20 pb-16">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Übersicht
        </Button>

        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories.map((category, index) => (
                <Badge key={index} variant="outline" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl">
                  {category}
                </Badge>
              ))}
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-600 bg-clip-text text-transparent mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center justify-between gap-3 text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-emerald-600" />
                <span>{formattedDate()}</span>
              </div>
              
              {/* Haftungsausschluss */}
              <div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center text-gray-600 text-sm gap-1 hover:bg-emerald-50 p-1 rounded-xl">
                      <Info className="h-4 w-4 text-emerald-600" />
                      <span className="hidden sm:inline">Haftungsausschluss</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Haftungsausschluss</AlertDialogTitle>
                      <AlertDialogDescription className="space-y-3">
                        <p>
                        Die Inhalte dieses Blogs dienen ausschließlich Informationszwecken. KaufPause übernimmt keine Haftung für die Vollständigkeit, Aktualität oder Richtigkeit der Inhalte. Die bereitgestellten Informationen stellen keine Rechtsberatung, Finanzberatung oder ähnliche Dienstleistungen dar. Bitte konsultieren Sie bei wichtigen Entscheidungen entsprechende Fachleute.
                        </p>
                        <p>
                          Erwähnte Drittanbieter-Tools stellen keine Empfehlung dar. Wir übernehmen keine Haftung für externe Services und erhalten keine Provisionen. Nutzerbeispiele können fiktiv sein. Inhalte werden unter Verwendung verschiedener Hilfsmittel erstellt und redaktionell geprüft.
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogAction className="rounded-xl">Verstanden</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
          
          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-8 shadow-xl hover:shadow-2xl transition-shadow duration-300">
            <img 
              src={post.imageUrl || 'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?q=80&w=800&auto=format&fit=crop'} 
              alt={post.title} 
              className="w-full h-auto object-cover max-h-96"
            />
          </div>
          
          {/* Article Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-gray-100">
            <div className="prose prose-emerald prose-lg max-w-none">
              {post.content ? (
                <MarkdownRenderer content={post.content} />
              ) : (
                <p className="text-gray-500 italic">Der Inhalt dieses Artikels wird gerade geladen...</p>
              )}
            </div>
          </div>
          
          {/* Share and Actions */}
          <div className="flex flex-wrap justify-between items-center gap-4 bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleShare('copy')} 
                className="gap-2 rounded-xl hover:bg-emerald-50 hover:border-emerald-200"
              >
                <Copy className="h-4 w-4" />
                <span className="hidden sm:inline">Link kopieren</span>
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 rounded-xl hover:bg-emerald-50 hover:border-emerald-200">
                    <Share2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Teilen</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuItem onClick={() => handleShare('facebook')} className="gap-2 cursor-pointer rounded-lg">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Facebook
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('twitter')} className="gap-2 cursor-pointer rounded-lg">
                    <Twitter className="h-4 w-4 text-blue-400" />
                    Twitter
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('linkedin')} className="gap-2 cursor-pointer rounded-lg">
                    <Linkedin className="h-4 w-4 text-blue-700" />
                    LinkedIn
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <Button 
              variant={isSaved ? "default" : "outline"} 
              size="sm" 
              className={`gap-2 rounded-xl ${isSaved ? "bg-emerald-600 hover:bg-emerald-700" : "hover:bg-emerald-50 hover:border-emerald-200"}`}
              onClick={toggleSaveArticle}
            >
              {isSaved ? (
                <>
                  <BookmarkCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">Gespeichert</span>
                </>
              ) : (
                <>
                  <Bookmark className="h-4 w-4" />
                  <span className="hidden sm:inline">Speichern</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost; 